import React, {useEffect, useMemo, useState} from 'react';
import ReactDOM from 'react-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import deLocale from '@fullcalendar/core/locales/de';

import {getCurrentUserId, getCurrentUserRole} from '../utils/authHelper';
import {getSickEntries} from '../services/SickDayService';
import {getHolidayEntries} from '../services/HolidayService';
import {getFlexDayEntries} from '../services/FlexDayService';
import {getPublicHolidays} from '../services/PublicHolidayServices';
import {getCompanyClosures} from '../services/CompanyClosureService';
import {closureDayMap, daysInSpan} from '../utils/closureDays';
import '../styles/Calendar.css';

export default function AbsenceCalendar() {
    const [events, setEvents] = useState([]);
    const [expanded, setExpanded] = useState(false);

    const userId = getCurrentUserId();
    const role = getCurrentUserRole();

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            await loadCalendarData(cancelled);
        };
        void run();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!expanded) return;
        const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [expanded]);

    const loadCalendarData = async (cancelled = false) => {
        const eventsList = [];

        try {
            const [holidays, sick_days, gleitzeitDays, publicHolidays, closures] = await Promise.all([
                getHolidayEntries({role, userId}),
                getSickEntries({role, userId}),
                getFlexDayEntries({role, userId}),
                getPublicHolidays(),
                // A closure the whole organisation sees; an employee's own view drops the
                // ones they are excepted from, because for them the day is not closed.
                getCompanyClosures().catch(() => [])
            ]);
            if (cancelled) return;

            const isAdmin = role === 'admin' || role === 'super_admin';
            const closedDays = closureDayMap(closures, isAdmin ? null : userId);
            const myHolidays = (isAdmin ? holidays : holidays.filter(e => e.userId === userId))
                .filter(e => e.approvalStatus !== 'rejected');
            const mySickDays = isAdmin ? sick_days : sick_days.filter(e => e.userId === userId);
            const myGleitzeit = (isAdmin ? gleitzeitDays : gleitzeitDays.filter(e => e.userId === userId))
                .filter(e => e.approvalStatus !== 'rejected');

            myHolidays.forEach(e => {
                daysInSpan(e.startDate, e.endDate).forEach(dateStr => {
                    // Leave entered before the closure was defined: flagged, never hidden —
                    // whether the day is given back is a decision about the Urlaubskonto.
                    const clash = closedDays.get(dateStr);
                    const who = (isAdmin && e.displayName) ? ` – ${e.displayName}` : '';
                    eventsList.push({
                        title: clash ? `Urlaub trotz Betriebsschließung${who}` : `Urlaub${who}`,
                        date: dateStr,
                        color: clash ? '#e65100' : '#4caf50',
                        extendedProps: {
                            type: clash ? 'holidayClash' : 'holiday',
                            displayName: e.displayName,
                            startDate: e.startDate,
                            endDate: e.endDate,
                            numberDays: e.numberDays,
                            closureDescription: clash ? clash.description : null,
                        }
                    });
                });
            });

            mySickDays.forEach(e => {
                const start = new Date(e.startDate);
                const end = new Date(e.endDate);
                for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                    const dateStr = new Date(d).toISOString().split('T')[0];
                    eventsList.push({
                        title: (isAdmin && e.displayName) ? `Krank – ${e.displayName}` : 'Krank',
                        date: dateStr,
                        color: '#f44336',
                        extendedProps: { type: 'sick', displayName: e.displayName, startDate: e.startDate, endDate: e.endDate, numberDays: e.numberDays }
                    });
                }
            });

            myGleitzeit.forEach(e => {
                const start = new Date(e.startDate);
                const end = new Date(e.endDate);
                for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                    const dateStr = new Date(d).toISOString().split('T')[0];
                    eventsList.push({
                        title: (isAdmin && e.displayName) ? `Flex Day – ${e.displayName}` : 'Flex Day',
                        date: dateStr,
                        color: '#ff9800',
                        extendedProps: { type: 'flexday', displayName: e.displayName, startDate: e.startDate, endDate: e.endDate, numberDays: e.numberDays }
                    });
                }
            });

            for (const [dateStr, closure] of closedDays.entries()) {
                eventsList.push({
                    title: closure.description
                        ? `Betriebsschließung – ${closure.description}`
                        : 'Betriebsschließung',
                    date: dateStr,
                    color: '#546e7a',
                    extendedProps: {
                        type: 'closure',
                        description: closure.description,
                        startDate: closure.start_date,
                        endDate: closure.end_date,
                    }
                });
            }

            publicHolidays.forEach(e => {
                eventsList.push({
                    title: `Feiertag – ${e.description}`,
                    date: e.date,
                    color: '#2196f3',
                    extendedProps: { type: 'publicHoliday', description: e.description }
                });
            });

            setEvents(eventsList);
        } catch (err) {
            console.error('Fehler beim Laden der Kalenderdaten:', err);
        }
    };

    const tooltipByDate = useMemo(() => {
        const map = new Map();
        for (const ev of events) {
            const date = ev.date;
            const type = ev.extendedProps?.type;
            let line = ev.title;
            if (type === 'closure' && ev.extendedProps) {
                const s = String(ev.extendedProps.startDate || '').slice(0, 10);
                const e = String(ev.extendedProps.endDate || '').slice(0, 10);
                if (s && e && s !== e) line = `${ev.title} — ${s} bis ${e}`;
            }
            if ((type === 'holiday' || type === 'holidayClash' || type === 'sick' || type === 'flexday') && ev.extendedProps) {
                const s = ev.extendedProps.startDate ? String(ev.extendedProps.startDate).slice(0, 10) : '';
                const e = ev.extendedProps.endDate ? String(ev.extendedProps.endDate).slice(0, 10) : '';
                const days = ev.extendedProps.numberDays != null ? ` (${ev.extendedProps.numberDays} Tage)` : '';
                if (s && e) line = `${ev.title} — ${s} bis ${e}${days}`;
                else line = `${ev.title}${days}`;
                if (type === 'holidayClash') {
                    line += ' — an diesem Tag ist Betriebsschließung, der Urlaubstag wird nicht benötigt';
                }
            }
            const arr = map.get(date) || [];
            arr.push(line);
            map.set(date, arr);
        }
        const result = new Map();
        for (const [date, lines] of map.entries()) {
            result.set(date, lines.join('\n'));
        }
        return result;
    }, [events]);

    const dayCellMount = (info) => {
        const dateStr = info.date.toISOString().slice(0, 10);
        const tooltip = tooltipByDate.get(dateStr);
        if (tooltip) info.el.title = tooltip;
        else info.el.removeAttribute('title');
    };

    return (
        <>
            <div className="calendar-wrapper">
                <div className="calendar-header">
                    <h3>Kalenderübersicht</h3>
                    <button
                        className="calendar-expand-btn"
                        onClick={() => setExpanded(true)}
                        aria-label="Kalender vergrößern"
                        title="Vergrößern"
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 3 21 3 21 9"/>
                            <polyline points="9 21 3 21 3 15"/>
                            <line x1="21" y1="3" x2="14" y2="10"/>
                            <line x1="3" y1="21" x2="10" y2="14"/>
                        </svg>
                    </button>
                </div>
                <FullCalendar
                    plugins={[dayGridPlugin]}
                    initialView="dayGridMonth"
                    locale={deLocale}
                    events={events}
                    height="auto"
                    dayMaxEvents={1}
                    dayCellDidMount={dayCellMount}
                />
            </div>

            {expanded && ReactDOM.createPortal(
                <div className="modal-backdrop" onClick={() => setExpanded(false)}>
                    <div className="calendar-modal" onClick={e => e.stopPropagation()}>
                        <div className="calendar-modal-header">
                            <h3>Kalenderübersicht</h3>
                            <button
                                className="calendar-modal-close"
                                onClick={() => setExpanded(false)}
                                aria-label="Schließen"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>
                        <div className="calendar-modal-body">
                            <FullCalendar
                                plugins={[dayGridPlugin]}
                                initialView="dayGridMonth"
                                locale={deLocale}
                                events={events}
                                height="100%"
                                dayMaxEvents={false}
                                dayCellDidMount={dayCellMount}
                            />
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
